import React, { useLayoutEffect, useRef, useState } from 'react'
import { KINDS, documentHeading, computeTotals, isOverdue } from '../lib/invoice'

/**
 * The document itself — what prints, and what the preview pane shows.
 *
 * One component for both. A separate "print version" is how a layout quietly
 * diverges from what the operator approved on screen, and the first anyone
 * notices is when a customer receives something they were never shown.
 *
 * ── It renders from figures, not from a database row ────────────────────────
 * `invoice` may be an unsaved draft straight out of the editor or a saved row
 * read back from Postgres. Totals are recomputed here from the lines either
 * way, which is what lets the preview move as the operator types. Once saved,
 * the caller passes the stored row and the two agree — computeTotals() is
 * written to the same formula as recalc_invoice_totals(), and invoice.test.mjs
 * checks it against figures taken out of the live database.
 */

const LOGO = '/assets/logos/coquis-logo.svg'

const ZAR = new Intl.NumberFormat('en-ZA', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})

// "R 1 234,56" — space after the R, space as the thousands separator, comma for
// the decimal. The South African convention, and what a local bookkeeper reads
// without having to think about it.
//
// A negative reads "− R 899,00" rather than "R -899,00": the minus belongs to
// the amount, not to the currency, and a hyphen tucked between the R and the
// digits is easy to miss on a credit note — which is precisely the document
// where missing it matters.
const rand = (value) => {
  const n = Number(value) || 0
  return n < 0 ? `− R ${ZAR.format(Math.abs(n))}` : `R ${ZAR.format(n)}`
}

const longDate = (value) => {
  if (!value) return '—'
  const [y, m, d] = String(value).slice(0, 10).split('-').map(Number)
  return new Intl.DateTimeFormat('en-ZA', { day: '2-digit', month: 'short', year: 'numeric' })
    .format(new Date(Date.UTC(y, m - 1, d)))
}

/** A quantity reads as "12", not "12.000" — but 1.5 hours must stay 1.5. */
const qty = (value) => {
  const n = Number(value) || 0
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(3)))
}

const Lines = ({ text }) =>
  String(text ?? '')
    .split('\n')
    .filter((line) => line.trim())
    .map((line, index) => <div key={index}>{line}</div>)

const InvoiceDocument = ({ invoice, items = [], issuer }) => {
  const meta = KINDS[invoice.kind] ?? KINDS.invoice
  const heading = documentHeading(invoice.kind, issuer.vatNumber)

  const totals = computeTotals({
    items,
    discountType: invoice.discount_type,
    discountValue: invoice.discount_value,
    shippingAmount: invoice.shipping_amount,
    taxRate: invoice.tax_rate,
    pricesIncludeTax: invoice.prices_include_tax,
  })

  const overdue = isOverdue(invoice)
  const stamp =
    invoice.status === 'draft' ? { text: 'DRAFT', color: '#b45309' }
    : invoice.status === 'cancelled' ? { text: 'CANCELLED', color: '#b91c1c' }
    : invoice.status === 'paid' ? { text: 'PAID', color: '#0ca30c' }
    : overdue ? { text: 'OVERDUE', color: '#b91c1c' }
    : null

  const balance = Number(totals.total) - Number(invoice.amount_paid || 0)
  const partlyPaid = Number(invoice.amount_paid || 0) > 0 && balance > 0.005

  const billToLocation = [invoice.bill_to_city, invoice.bill_to_province, invoice.bill_to_postal_code]
    .filter(Boolean)
    .join(', ')

  return (
    <article className="sheet" aria-label={`${heading} ${invoice.number ?? ''}`}>
      {stamp && (
        <div className="sheet-stamp" style={{ color: stamp.color }} aria-hidden="true">
          {stamp.text}
        </div>
      )}

      {/* ---- Header ------------------------------------------------------ */}
      <header className="flex items-start justify-between gap-8">
        <div>
          <img
            src={LOGO}
            alt="Coquis Men's Outfitters"
            style={{ height: '17mm', width: 'auto' }}
            /* The mark is the shop's identity, not decoration, so it is never
               allowed to fail silently to a broken-image box. */
            onError={(event) => {
              event.currentTarget.style.display = 'none'
              event.currentTarget.nextElementSibling.style.display = 'block'
            }}
          />
          <div
            style={{
              display: 'none',
              fontFamily: 'Montserrat, sans-serif',
              fontSize: '17pt',
              fontWeight: 700,
              color: '#2c3e50',
              letterSpacing: '0.04em',
            }}
          >
            COQUIS
          </div>
          <div style={{ marginTop: '2.5mm', color: 'var(--sheet-soft)', fontSize: '7.6pt' }}>
            <div style={{ fontWeight: 600, color: 'var(--sheet-ink)' }}>{issuer.name}</div>
            <Lines text={issuer.address} />
            {issuer.phone && <div>{issuer.phone}</div>}
            {issuer.email && <div>{issuer.email}</div>}
            {issuer.vatNumber && <div>VAT no. {issuer.vatNumber}</div>}
            {issuer.regNumber && <div>Reg. no. {issuer.regNumber}</div>}
          </div>
        </div>

        <div style={{ textAlign: 'right', minWidth: '62mm' }}>
          <h1
            style={{
              fontSize: '18pt',
              fontWeight: 700,
              color: '#2c3e50',
              letterSpacing: '0.02em',
              lineHeight: 1.1,
            }}
          >
            {heading}
          </h1>
          <div
            style={{
              fontSize: '10.5pt',
              fontWeight: 600,
              letterSpacing: '0.04em',
              marginTop: '0.6mm',
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {invoice.number ?? 'Not yet numbered'}
          </div>

          <table style={{ marginTop: '4mm', marginLeft: 'auto', fontSize: '7.8pt' }}>
            <tbody>
              <MetaRow label="Date" value={longDate(invoice.issue_date)} />
              {meta.owed && invoice.due_date && (
                <MetaRow
                  label="Due"
                  value={longDate(invoice.due_date)}
                  emphasis={overdue ? '#b91c1c' : undefined}
                />
              )}
              {invoice.kind === 'quote' && invoice.due_date && (
                <MetaRow label="Valid until" value={longDate(invoice.due_date)} />
              )}
              {invoice.reference && <MetaRow label="Your reference" value={invoice.reference} />}
            </tbody>
          </table>
        </div>
      </header>

      <div className="sheet-rule" style={{ marginTop: '5mm' }} />

      {/* ---- Addressee --------------------------------------------------- */}
      <section style={{ marginTop: '6mm', display: 'flex', gap: '8mm', alignItems: 'flex-start' }}>
        <div className="sheet-party" style={{ flex: '1 1 0', minWidth: 0 }}>
          <div className="sheet-label">
            {invoice.kind === 'credit_note' ? 'Credit to' : meta.owed ? 'Bill to' : 'Prepared for'}
          </div>
          <div style={{ fontSize: '10pt', fontWeight: 600, marginTop: '1mm' }}>
            {invoice.bill_to_name || '—'}
          </div>
          <div style={{ color: 'var(--sheet-soft)', marginTop: '0.8mm' }}>
            {invoice.bill_to_contact && <div>Attn: {invoice.bill_to_contact}</div>}
            <Lines text={invoice.bill_to_address} />
            {billToLocation && <div>{billToLocation}</div>}
            {invoice.bill_to_country && invoice.bill_to_country !== 'South Africa' && (
              <div>{invoice.bill_to_country}</div>
            )}
            {invoice.bill_to_email && <div>{invoice.bill_to_email}</div>}
            {invoice.bill_to_phone && <div>{invoice.bill_to_phone}</div>}
            {invoice.bill_to_vat_number && <div>VAT no. {invoice.bill_to_vat_number}</div>}
            {invoice.bill_to_reg_number && <div>Reg. no. {invoice.bill_to_reg_number}</div>}
          </div>
        </div>
      </section>

      {/* ---- Lines ------------------------------------------------------- */}
      <section style={{ marginTop: '6mm' }}>
        <table className="sheet-table">
          <thead>
            <tr>
              <th style={{ width: '52%' }}>Description</th>
              <th className="sheet-num" style={{ width: '11%' }}>Qty</th>
              <th className="sheet-num" style={{ width: '18%' }}>
                Unit price{invoice.prices_include_tax ? '' : ' (excl.)'}
              </th>
              <th className="sheet-num" style={{ width: '19%' }}>Amount</th>
            </tr>
          </thead>
          <tbody>
            {totals.lines.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', color: 'var(--sheet-soft)', padding: '8mm 0' }}>
                  No lines on this document yet.
                </td>
              </tr>
            ) : (
              totals.lines.map((line, index) => (
                <tr key={line.key ?? line.id ?? index}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{line.description}</div>
                    {(line.detail || line.sku) && (
                      <div style={{ color: 'var(--sheet-soft)', fontSize: '7.4pt', marginTop: '0.4mm' }}>
                        {[line.sku, line.detail].filter(Boolean).join(' · ')}
                      </div>
                    )}
                  </td>
                  <td className="sheet-num">
                    {qty(line.quantity)}
                    {line.unit ? ` ${line.unit}` : ''}
                  </td>
                  <td className="sheet-num">{rand(line.unit_price)}</td>
                  <td className="sheet-num" style={{ fontWeight: 500 }}>{rand(line.lineTotal)}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </section>

      {/* ---- Totals ------------------------------------------------------ */}
      <section
        className="sheet-totals"
        style={{ marginTop: '5mm', display: 'flex', justifyContent: 'flex-end' }}
      >
        <div style={{ width: '76mm' }}>
          <div className="sheet-total-row">
            <span style={{ color: 'var(--sheet-soft)' }}>Subtotal</span>
            <span className="sheet-num">{rand(totals.subtotal)}</span>
          </div>

          {totals.discountAmount > 0 && (
            <div className="sheet-total-row">
              <span style={{ color: 'var(--sheet-soft)' }}>
                Discount
                {invoice.discount_type === 'percent' ? ` (${Number(invoice.discount_value)}%)` : ''}
              </span>
              <span className="sheet-num">− {rand(totals.discountAmount)}</span>
            </div>
          )}

          {totals.shippingAmount > 0 && (
            <div className="sheet-total-row">
              <span style={{ color: 'var(--sheet-soft)' }}>Delivery</span>
              <span className="sheet-num">{rand(totals.shippingAmount)}</span>
            </div>
          )}

          {/* On an exclusive document VAT is a line that gets added; on an
              inclusive one it is a disclosure of tax already in the total.
              Printing the two identically is what makes a customer query an
              invoice, so they are worded differently on purpose. */}
          {invoice.prices_include_tax ? (
            <div className="sheet-total-row" style={{ fontSize: '7.6pt', color: 'var(--sheet-soft)' }}>
              <span>Includes VAT @ {Number(invoice.tax_rate)}%</span>
              <span className="sheet-num">{rand(totals.taxAmount)}</span>
            </div>
          ) : (
            <>
              <div className="sheet-total-row">
                <span style={{ color: 'var(--sheet-soft)' }}>Total excluding VAT</span>
                <span className="sheet-num">{rand(totals.netBeforeTax)}</span>
              </div>
              <div className="sheet-total-row">
                <span style={{ color: 'var(--sheet-soft)' }}>VAT @ {Number(invoice.tax_rate)}%</span>
                <span className="sheet-num">{rand(totals.taxAmount)}</span>
              </div>
            </>
          )}

          <div className="sheet-total-grand">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: '6mm' }}>
              {/* Nothing is "due" on a quote — it is an offer nobody has accepted
                  yet. Saying so invites a customer to pay against it, which then
                  has to be receipted against an invoice that does not exist. */}
              <span style={{ fontSize: '7.4pt', letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.85 }}>
                {invoice.kind === 'credit_note' ? 'Total credit'
                  : invoice.kind === 'quote' ? 'Quoted total'
                  : 'Total due'}
              </span>
              <span style={{ fontSize: '13pt', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>
                {rand(totals.total)}
              </span>
            </div>
          </div>

          {partlyPaid && (
            <>
              <div className="sheet-total-row" style={{ marginTop: '1.5mm' }}>
                <span style={{ color: 'var(--sheet-soft)' }}>Paid to date</span>
                <span className="sheet-num">− {rand(invoice.amount_paid)}</span>
              </div>
              <div className="sheet-total-row" style={{ fontWeight: 700, borderTop: '0.3mm solid var(--sheet-rule)' }}>
                <span>Balance outstanding</span>
                <span className="sheet-num">{rand(balance)}</span>
              </div>
            </>
          )}

          {invoice.currency && invoice.currency !== 'ZAR' && (
            <div style={{ textAlign: 'right', fontSize: '7pt', color: 'var(--sheet-soft)', marginTop: '1mm' }}>
              All amounts in {invoice.currency}
            </div>
          )}
        </div>
      </section>

      {invoice.notes && (
        <section style={{ marginTop: '6mm' }}>
          <div className="sheet-label">Notes</div>
          <div style={{ marginTop: '1mm', whiteSpace: 'pre-wrap' }}>{invoice.notes}</div>
        </section>
      )}

      {/* ---- Foot -------------------------------------------------------- */}
      {/* marginTop:auto pushes this to the bottom of a short document, so a
          two-line invoice does not leave the banking details floating in the
          middle of an otherwise empty page. */}
      <footer className="sheet-foot" style={{ marginTop: 'auto', paddingTop: '8mm' }}>
        <hr className="sheet-hairline" />
        <div style={{ display: 'flex', gap: '8mm', marginTop: '4mm' }}>
          {meta.owed && issuer.bankDetails && (
            <div style={{ flex: '1 1 0', minWidth: 0 }}>
              <div className="sheet-label">How to pay</div>
              <div style={{ marginTop: '1mm', color: 'var(--sheet-soft)' }}>
                <Lines text={issuer.bankDetails} />
                {/* Only once there is a number to quote. On an unsaved draft this
                    would print "Please use as the payment reference", which is
                    what the preview showed before anyone noticed. */}
                {invoice.number && (
                  <div style={{ marginTop: '1mm', color: 'var(--sheet-ink)' }}>
                    Please use <strong>{invoice.number}</strong> as the payment reference.
                  </div>
                )}
              </div>
            </div>
          )}

          {invoice.terms && (
            <div style={{ flex: '1 1 0', minWidth: 0 }}>
              <div className="sheet-label">Terms</div>
              <div style={{ marginTop: '1mm', color: 'var(--sheet-soft)', whiteSpace: 'pre-wrap' }}>
                {invoice.terms}
              </div>
            </div>
          )}
        </div>

        <div
          style={{
            marginTop: '5mm',
            paddingTop: '2.5mm',
            borderTop: '0.25mm solid var(--sheet-rule)',
            display: 'flex',
            justifyContent: 'space-between',
            gap: '6mm',
            fontSize: '6.8pt',
            color: 'var(--sheet-soft)',
          }}
        >
          <span>
            {issuer.name}
            {issuer.regNumber ? ` · Reg. ${issuer.regNumber}` : ''}
            {issuer.vatNumber ? ` · VAT ${issuer.vatNumber}` : ''}
          </span>
          <span>
            {heading} {invoice.number} · {longDate(invoice.issue_date)}
          </span>
        </div>
      </footer>
    </article>
  )
}

const MetaRow = ({ label, value, emphasis }) => (
  <tr>
    <td style={{ paddingRight: '4mm', color: 'var(--sheet-soft)', whiteSpace: 'nowrap' }}>{label}</td>
    <td style={{ fontWeight: 600, whiteSpace: 'nowrap', color: emphasis }}>{value}</td>
  </tr>
)

/**
 * Shows the sheet at true A4 and scales it to whatever space it has.
 *
 * One layout, measured once in millimetres, shrunk to fit — rather than a second
 * screen-sized approximation of the page that drifts from it over time. The
 * scale is recalculated on resize, so the preview stays honest when the sidebar
 * collapses or the window changes.
 */
export const SheetPreview = ({ children, maxWidth = 210 }) => {
  const wrapRef = useRef(null)
  const [scale, setScale] = useState(0.5)
  const [height, setHeight] = useState(0)

  useLayoutEffect(() => {
    const element = wrapRef.current
    if (!element) return

    const measure = () => {
      const available = element.clientWidth
      if (!available) return
      // 210mm in CSS px, measured rather than assumed — the ratio is fixed at
      // 96dpi but reading it keeps this correct if that ever stops being true.
      const probe = document.createElement('div')
      probe.style.cssText = `width:${maxWidth}mm;position:absolute;visibility:hidden`
      element.appendChild(probe)
      const sheetPx = probe.getBoundingClientRect().width
      probe.remove()
      if (!sheetPx) return

      const next = Math.min(1, available / sheetPx)
      setScale(next)

      // offsetHeight, not getBoundingClientRect: the former is the untransformed
      // layout height, so it does not feed the scale we just applied back into
      // the next measurement and shrink the container on every pass.
      const sheet = element.querySelector('.sheet')
      if (sheet) setHeight(sheet.offsetHeight * next)
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxWidth, children])

  return (
    <div ref={wrapRef} className="sheet-preview" style={{ '--sheet-scale': scale, height: height || undefined }}>
      {children}
    </div>
  )
}

/**
 * Wait for images before printing.
 *
 * window.print() captures the page as it stands. Called while the logo is still
 * in flight, it produces a PDF with a hole where the mark should be — and that
 * PDF is what gets e-mailed, because nobody reprints to check.
 */
export async function printWhenReady(root = document) {
  const images = [...root.querySelectorAll('img')]
  await Promise.all(
    images.map((image) =>
      image.complete
        ? Promise.resolve()
        : new Promise((resolve) => {
            image.addEventListener('load', resolve, { once: true })
            // An image that never arrives must not block printing for ever. The
            // document has a text fallback for the logo; two seconds is long
            // enough to be sure it is not merely slow.
            image.addEventListener('error', resolve, { once: true })
            setTimeout(resolve, 2000)
          }),
    ),
  )
  if (document.fonts?.ready) await document.fonts.ready
  window.print()
}

export default InvoiceDocument
